// Лёгкая fetch-обёртка для API АстраМаркет.

import { ApiError } from "./types";
import { clearToken, getToken } from "./authStorage";

// API_BASE по приоритету:
// 1. VITE_API_BASE из env (если задан явно при билде)
// 2. relative "/api/v1" — резолвится через nginx-edge / VK Balancer на тот же origin,
//    что и фронт. Это рабочий вариант для prod (https://rb.maglab.tech) и docker-стэка.
// 3. для dev-сервера vite (порт 5173) Vite proxy в vite.config.ts мог бы перепроксить,
//    но прямой URL в браузере на dev = http://localhost:5290 → SPA → /api/v1 → nginx → backend.
export const API_BASE: string =
  ((import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE) ||
  "/api/v1";

type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

type RequestOptions = {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Не редиректить на /login при 401 (например, сам /auth/me на старте). */
  skipAuthRedirect?: boolean;
};

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const base = API_BASE.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  const url = `${base}${p}`;
  if (!query) return url;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${url}?${qs}` : url;
}

function redirectToLogin(): void {
  // Простой способ — перезагрузка на /login. AuthContext подхватит и подчистит state.
  if (typeof window !== "undefined") {
    const here = window.location.pathname + window.location.search;
    if (!here.startsWith("/login")) {
      window.location.href = "/login";
    }
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const url = buildUrl(path, opts.query);

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  let resp: Response;
  try {
    resp = await fetch(url, { method, headers, body, signal: opts.signal });
  } catch (e) {
    // network-уровневая ошибка
    throw new ApiError(0, "network_error", (e as Error).message || "Сеть недоступна");
  }

  // 204 / 205 — без тела
  if (resp.status === 204 || resp.status === 205) {
    return undefined as unknown as T;
  }

  const ct = resp.headers.get("content-type") || "";
  let payload: unknown;
  if (ct.includes("application/json")) {
    try {
      payload = await resp.json();
    } catch {
      payload = null;
    }
  } else {
    try {
      payload = await resp.text();
    } catch {
      payload = null;
    }
  }

  if (!resp.ok) {
    let code = "http_error";
    let message = `HTTP ${resp.status}`;
    let details: unknown = null;
    if (payload && typeof payload === "object") {
      const p = payload as { code?: string; message?: string; detail?: unknown; details?: unknown };
      if (typeof p.code === "string") code = p.code;
      if (typeof p.message === "string") message = p.message;
      else if (typeof p.detail === "string") message = p.detail;
      details = p.details ?? p.detail ?? null;
    }

    if (resp.status === 401) {
      clearToken();
      if (!opts.skipAuthRedirect) redirectToLogin();
    }

    throw new ApiError(resp.status, code, message, details);
  }

  return payload as T;
}
